import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ChevronLeft, ChevronRight, ImageOff, X } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { dashboardApi, AiUsageDailyDetailData, AiUsageDailyDetailItem } from '@/api/dashboardApi';

export interface DailyDetailTarget {
  startDate: string;
  endDate: string;
  sourceType?: string;
  channel?: string;
  label: string;
}

interface Props {
  target: DailyDetailTarget | null;
  onClose: () => void;
  filterUserId?: string;
  isSuperAdmin?: boolean;
  isTeamView?: boolean;
}

const SOURCE_TYPE_LABEL: Record<string, string> = {
  WEBSITE_CRAWLING: '웹사이트 크롤링',
  IMAGE_URL: '이미지 URL',
  IMAGE_UPLOAD: '이미지 업로드',
};

const CHANNEL_LABEL: Record<string, string> = {
  INNER: '직접',
  OPEN_API: '연동',
  SCHEDULED: '정기크롤링',
  UNKNOWN: '알 수 없음',
};

const PAGE_SIZE = 20;

export function AiUsageDailyDetailPanel({ target, onClose, filterUserId, isSuperAdmin, isTeamView }: Props) {
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AiUsageDailyDetailData | null>(null);

  const fetchPage = useCallback(async (p: number) => {
    if (!target) return;
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardApi.getAiUsageDailyDetail({
        startDate: target.startDate,
        endDate: target.endDate,
        sourceType: target.sourceType,
        channel: target.channel,
        userId: isSuperAdmin && filterUserId ? filterUserId : undefined,
        teamView: isTeamView,
        page: p,
        size: PAGE_SIZE,
      });
      setData(result);
    } catch (e: any) {
      setError(e.response?.data?.error?.message || e.message || '상세 이력 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [target, filterUserId, isSuperAdmin, isTeamView]);

  useEffect(() => {
    if (target) {
      setPage(0);
      fetchPage(0);
    } else {
      setData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  const goToPage = (p: number) => {
    setPage(p);
    fetchPage(p);
  };

  const periodLabel = target.startDate === target.endDate ? target.startDate : `${target.startDate} ~ ${target.endDate}`;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">{periodLabel} · {target.label}</CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              이 숫자의 실제 근거가 된 개별 생성 이력입니다.
              {data && ` 총 ${data.totalElements.toLocaleString()}건`}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} className="flex-shrink-0">
            <X className="w-4 h-4 mr-1" /> 닫기
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner size="md" />
          </div>
        ) : data && data.items.length > 0 ? (
          <div className="space-y-2">
            {data.items.map((item, idx) => (
              <DetailRow key={`${item.imageSrc}-${item.createdAt}-${idx}`} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-400 py-10">조회된 이력이 없습니다.</p>
        )}

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4 mt-2 border-t">
            <Button variant="outline" size="sm" disabled={page <= 0 || loading} onClick={() => goToPage(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600">{page + 1} / {data.totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= data.totalPages - 1 || loading} onClick={() => goToPage(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DetailRow({ item }: { item: AiUsageDailyDetailItem }) {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="flex gap-3 border rounded-lg p-3 items-start">
      <div className="w-16 h-16 flex-shrink-0 rounded-md border bg-gray-50 overflow-hidden flex items-center justify-center">
        {!imgFailed ? (
          <img
            src={item.imageSrc}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <ImageOff className="w-5 h-5 text-gray-300" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge className={item.channel === 'OPEN_API' ? 'bg-purple-100 text-purple-700' : item.channel === 'SCHEDULED' ? 'bg-emerald-100 text-emerald-700' : item.channel === 'INNER' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}>
            {CHANNEL_LABEL[item.channel] || item.channel}
          </Badge>
          <Badge className="bg-gray-100 text-gray-600">
            {SOURCE_TYPE_LABEL[item.sourceType] || item.sourceType}
          </Badge>
          <span className="text-xs text-gray-400 font-mono">{item.createdAt}</span>
        </div>
        <p className="text-sm text-gray-800 break-words">
          {item.generatedAlt || <span className="text-gray-400">(ALT 텍스트 없음)</span>}
        </p>
        <a
          href={item.imageSrc}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-primary hover:underline break-all block"
        >
          {item.imageSrc}
        </a>
      </div>
    </div>
  );
}
